import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { Domains } from '../../constants';
import { Hasher } from '../hasher';
import {
  DomainPreferences,
  HasDependencies,
  hashString,
  LatestDomainPreferences,
  Role,
} from '../types';
import { handleGQLErrors } from '../utils';
import {
  DomainPreferencesFetchError,
  DomainPreferencesUpdateError,
} from './errors';

export interface DomainPreferencesService extends HasDependencies {
  getLatestWithVersion: (domain: Domains) => Promise<LatestDomainPreferences>;
  getLatest: (domain: Domains) => Promise<DomainPreferences>;
  update: (
    domain: Domains,
    preferences: DomainPreferences
  ) => Promise<DomainPreferences>;
}

export class BackendDomainPreferencesService
  implements DomainPreferencesService
{
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      Hasher.hash(this.simulateRole),
    ]),
  ];

  public getLatestWithVersion = async (
    domain: Domains
  ): Promise<LatestDomainPreferences> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.domainPreferencesLatestVersion({
        domain,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.domainPreferencesLatestVersion;
    if (!result) {
      throw new DomainPreferencesFetchError(
        domain,
        new Error('response is null')
      );
    }
    return result as LatestDomainPreferences;
  };

  public getLatest = async (domain: Domains): Promise<DomainPreferences> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.domainPreferencesLatestVersion({
        domain,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.domainPreferencesLatestVersion;
    if (!result) {
      throw new DomainPreferencesFetchError(
        domain,
        new Error('response is null')
      );
    }
    return result.settings as DomainPreferences;
  };

  public update = async (
    domain: Domains,
    preferences: DomainPreferences
  ): Promise<DomainPreferences> => {
    const response = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.createNewDomainPreferences({
        domain,
        preferences,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    const result = response.createNewDomainPreferences;
    if (!result) {
      throw new DomainPreferencesUpdateError(
        domain,
        new Error('response is null')
      );
    }
    return result.settings as DomainPreferences;
  };
}
